import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractModificationGround,
  ContractModificationPreviewDto,
  FieldErrorDto,
  useSubmitContractModificationNoticeMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { NumericInput } from "../../../common/input/NumericInput";
import { DoffinNoAnswerAlert } from "../result-notice/DoffinNoAnswerAlert";
import { DoffinRefusalAlert, doffinRefusal } from "../result-notice/DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "../result-notice/doffinNoAnswer";
import { exceedsHalfOfOriginalValue, MODIFICATION_GROUNDS, regimeOf } from "./modificationGrounds";
import { useModificationTexts } from "./useModificationTexts";

interface Props {
  eventId: string;
  preview: ContractModificationPreviewDto;
  onClose: () => void;
}

export const ContractModificationModal = ({ eventId, preview, onClose }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const { groundLabel, conditions } = useModificationTexts();
  const regime = regimeOf(preview.form);
  const [bidId, setBidId] = useState(preview.contracts.length === 1 ? preview.contracts[0].bidId : "");
  const contract = preview.contracts.find((candidate) => candidate.bidId === bidId);
  const [ground, setGround] = useState<ContractModificationGround>(ContractModificationGround.AdditionalDeliveries);
  const [conditionsConfirmed, setConditionsConfirmed] = useState(false);
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [originalValue, setOriginalValue] = useState<number | null>(contract?.originalValue?.value ?? null);
  const [valueBefore, setValueBefore] = useState<number | null>(contract?.currentValue?.value ?? null);
  const [valueAfter, setValueAfter] = useState<number | null>(null);
  const [showMissing, setShowMissing] = useState(false);
  const [refusal, setRefusal] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const [submit, { isLoading }] = useSubmitContractModificationNoticeMutation();

  const chooseContract = (next: string) => {
    setBidId(next);
    const chosen = preview.contracts.find((candidate) => candidate.bidId === next);
    setOriginalValue(chosen?.originalValue?.value ?? null);
    setValueBefore(chosen?.currentValue?.value ?? null);
  };

  const exceedsCap = preview.capsPriceIncrease && exceedsHalfOfOriginalValue(originalValue, valueBefore, valueAfter);
  const missing = {
    contract: !bidId,
    description: !description.trim(),
    reason: !reason.trim(),
    originalValue: preview.capsPriceIncrease && !originalValue,
    valueBefore: !valueBefore,
    valueAfter: !valueAfter,
    conditions: !conditionsConfirmed,
  };
  const complete = !Object.values(missing).some(Boolean);

  const send = async () => {
    setShowMissing(true);
    if (!complete || exceedsCap || valueBefore === null || valueAfter === null) return;
    setRefusal(null);
    setNoAnswer(null);
    try {
      await submit({
        sourcingEventId: eventId,
        submitContractModificationNoticeRequest: {
          bidId,
          ground,
          description,
          reason,
          valueBefore,
          valueAfter,
          conditionsConfirmed,
          originalValue: preview.capsPriceIncrease ? (originalValue ?? undefined) : undefined,
        },
      }).unwrap();
      toast({ title: t("The modification notice is sent to Doffin"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      const breaches = doffinRefusal(e);
      const unanswered = doffinNoAnswer(e);
      if (breaches) setRefusal(breaches);
      else if (unanswered) setNoAnswer(unanswered);
      else trigger(e);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">{t("Announce contract modification")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing="5">
            {preview.contracts.length > 1 && (
              <FormControl isRequired isInvalid={showMissing && missing.contract}>
                <FormLabel>{t("Contract")}</FormLabel>
                <Select
                  value={bidId}
                  placeholder={t("Choose the contract that changes") ?? undefined}
                  onChange={(e) => chooseContract(e.target.value)}
                >
                  {preview.contracts.map((candidate) => (
                    <option key={candidate.bidId} value={candidate.bidId}>
                      {candidate.supplierName}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{t("zz_FieldErrorCode_Required")}</FormErrorMessage>
              </FormControl>
            )}
            <FormControl as="fieldset" isRequired>
              <FormLabel as="legend">{t("Ground for the change")}</FormLabel>
              <RadioGroup
                value={ground}
                onChange={(next) => {
                  setGround(next as ContractModificationGround);
                  setConditionsConfirmed(false);
                }}
              >
                <Stack>
                  {MODIFICATION_GROUNDS.map((option) => (
                    <Radio key={option} value={option}>
                      {groundLabel(option)}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
            <Box borderWidth="1px" borderColor="smBorder" rounded="md" p="3" bg="smBackgroundSecondary">
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {conditions(ground, regime)}
              </Text>
            </Box>
            <FormControl isRequired isInvalid={showMissing && missing.conditions}>
              <Checkbox isChecked={conditionsConfirmed} onChange={(e) => setConditionsConfirmed(e.target.checked)}>
                {t("The conditions above are met")}
              </Checkbox>
              <FormErrorMessage>{t("zz_FieldErrorCode_Required")}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={showMissing && missing.description}>
              <FormLabel>{t("Description of the change")}</FormLabel>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
              <FormErrorMessage>{t("zz_FieldErrorCode_Required")}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={showMissing && missing.reason}>
              <FormLabel>{t("Reason for the change")}</FormLabel>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
              <FormErrorMessage>{t("zz_FieldErrorCode_Required")}</FormErrorMessage>
            </FormControl>
            {preview.capsPriceIncrease && (
              <FormControl isRequired isInvalid={showMissing && missing.originalValue}>
                <FormLabel>{t("Original contract value")}</FormLabel>
                <NumericInput numericValue={originalValue} onNumericChange={setOriginalValue} />
                <FormHelperText>
                  {t("From the contract award notice. The 50 % limit is measured against this value for every change. Use the index-adjusted price if the contract has a price index clause (FOA § 28-1 (2)).")}
                </FormHelperText>
                <FormErrorMessage>{t("zz_FieldErrorCode_Required")}</FormErrorMessage>
              </FormControl>
            )}
            <Flex gap="4" direction={{ base: "column", md: "row" }}>
              <FormControl isRequired isInvalid={showMissing && missing.valueBefore}>
                <FormLabel>{t("Contract value before the change")}</FormLabel>
                <NumericInput numericValue={valueBefore} onNumericChange={setValueBefore} />
                <FormHelperText>{t("From the contract award notice or the last modification.")}</FormHelperText>
                <FormErrorMessage>{t("zz_FieldErrorCode_Required")}</FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={(showMissing && missing.valueAfter) || exceedsCap}>
                <FormLabel>{t("Contract value after the change")}</FormLabel>
                <NumericInput numericValue={valueAfter} onNumericChange={setValueAfter} />
                <FormErrorMessage>
                  {exceedsCap ? t("zz_DoffinApiErrorCode_ModificationExceedsHalfOfContractValue") : t("zz_FieldErrorCode_Required")}
                </FormErrorMessage>
              </FormControl>
            </Flex>
            {refusal && (
              <DoffinRefusalAlert
                breaches={refusal}
                heading={t("Doffin refused the contract modification notice. Nothing has been announced.")}
              />
            )}
            {noAnswer && <DoffinNoAnswerAlert noAnswer={noAnswer} />}
          </Stack>
          <Flex justify="end" pt="6">
            <Button
              colorScheme="teal"
              isLoading={isLoading}
              isDisabled={exceedsCap || noAnswer === "whileSending"}
              onClick={send}
            >
              {t("Announce on Doffin")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
